import React from 'react';
import PropTypes from 'prop-types';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { UserManager } from 'oidc-client-ts';
import Loading from '../components/loading';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import Organization from '../models/organization';

const FetchContext = React.createContext();

export const useFetch = () => React.useContext(FetchContext);

const AuthenticationInner = ({ children }) => {
    const auth = useAuth();
    const history = useHistory();
    const [resolvingLanding, setResolvingLanding] = React.useState(false);

    React.useEffect(() => {
        if (auth.isAuthenticated && sessionStorage.getItem('landing_check')) {
            sessionStorage.removeItem('landing_check');
            setResolvingLanding(true);
            resolveEventCreationUrl(auth.user.access_token)
                .then(dest => {
                    history.replace(dest);
                })
                .catch(() => {
                    history.replace('/ticket/');
                })
                .finally(() => {
                    setResolvingLanding(false);
                });
        }
    }, [auth.isAuthenticated, auth.user, history]);

    // Recover from silent renew / refresh-token expiry.
    // Without this, `auth.error` leaves the UI stuck on a spinner until the user manually refreshes.
    // Clear the stale user and bounce through sign-in, preserving the current path via url_state.
    React.useEffect(() => {
        if (!auth.error) return;
        if (sessionStorage.getItem('landing_check')) return; // handled by the landing branch below
        auth.removeUser()
            .catch(err => console.error('[Auth] removeUser failed', err))
            .finally(() => {
                auth.signinRedirect({
                    url_state: String(window.location.pathname + window.location.search)
                });
            });
    }, [auth.error, auth]);

    if (auth.isLoading || resolvingLanding) {
        return <Loading active />;
    }

    if (auth.error) {
        if (sessionStorage.getItem('landing_check')) {
            sessionStorage.removeItem('landing_check');
            return <Redirect to="/ticket/landing?action=register" />;
        }
        // The effect above is redirecting; keep the spinner up during the OIDC round-trip.
        return <Loading active />;
    }

    if (auth.isAuthenticated) {
        if (sessionStorage.getItem('landing_check')) {
            return <Loading active />;
        }
        // Create a fetch alias that injects the token
        const fetch = (url, opts) => {
            opts = opts || {};
            const headers = (opts.headers = opts.headers || {});
            headers.Authorization = `${auth.user.token_type} ${auth.user.access_token}`;
            return window.fetch(url, opts);
        };

        // Inject a fetch command in a context
        return <FetchContext.Provider value={fetch}>{children}</FetchContext.Provider>;
    }
    return <SigninRedirect />;
};

const SigninRedirect = () => {
    const auth = useAuth();
    React.useEffect(() => {
        if (sessionStorage.getItem('landing_check')) {
            auth.signinRedirect({ prompt: 'none' });
        } else {
            auth.signinRedirect({
                url_state: String(window.location.pathname)
            });
        }
    }, [auth]);
    return null;
};

export const Authentication = ({ children, config }) => {
    const history = useHistory();

    const onSigninCallback = user => {
        if (sessionStorage.getItem('landing_check')) {
            window.history.replaceState({}, '', '/ticket/');
            return;
        }
        const dest = user?.url_state || '/ticket/';
        history.push(dest);
    };

    return (
        <AuthProvider
            automaticSilentRenew
            authority={config.OIDCUrl}
            redirect_uri={`${window.location.origin}/ticket/`}
            onSigninCallback={onSigninCallback}
            client_id={config.clientId}
        >
            <AuthenticationInner>{children}</AuthenticationInner>
        </AuthProvider>
    );
};

Authentication.propTypes = {
    children: PropTypes.node.isRequired,
    config: PropTypes.shape({
        OIDCUrl: PropTypes.string,
        clientId: PropTypes.string
    }).isRequired
};

const RegistrationSigninRedirect = () => {
    const auth = useAuth();
    const authRef = React.useRef(auth);
    React.useEffect(() => {
        authRef.current
            .removeUser()
            .then(() => authRef.current.signinRedirect())
            .catch(err => console.error('[Registration] removeUser failed', err));
    }, []);
    return null;
};

const RegistrationInner = () => {
    const auth = useAuth();

    if (auth.isLoading || auth.error) {
        return <Loading active />;
    }

    if (auth.isAuthenticated) {
        return <Redirect to="/ticket/" />;
    }

    return <RegistrationSigninRedirect />;
};

const resolveEventCreationUrl = token =>
    window
        .fetch(`${window.weezConfig.api.apiUrl}${Organization.listUrl()}?limit=1`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        .then(r => r.json())
        .then(data => {
            const orgId = data?.[0]?.id;
            return orgId ? `/ticket/O${orgId}/events/new` : '/ticket/';
        })
        .catch(() => '/ticket/');

export const Registration = ({ config }) => {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const [status, setStatus] = React.useState(() => {
        if (params.get('action') === 'register') return 'register';
        if (params.has('code') && params.has('state')) return 'callback';
        return 'redirect';
    });
    const [destination, setDestination] = React.useState(null);

    React.useEffect(() => {
        if (status === 'redirect') {
            sessionStorage.setItem('landing_check', '1');
            history.replace('/ticket/');
            return;
        }
        if (status === 'callback') {
            const manager = new UserManager({
                authority: config.OIDCUrl,
                client_id: config.clientIdRegister,
                redirect_uri: `${window.location.origin}/ticket/landing`
            });
            manager
                .signinCallback()
                .then(user => {
                    if (user?.access_token) {
                        return resolveEventCreationUrl(user.access_token).then(dest => {
                            setDestination(dest);
                            setStatus('authenticated');
                        });
                    }
                    window.history.replaceState({}, '', '/ticket/landing');
                    setStatus('register');
                })
                .catch(() => {
                    window.history.replaceState({}, '', '/ticket/landing');
                    setStatus('register');
                });
        }
    }, [status, config, history]);

    if (status === 'redirect' || status === 'callback') return <Loading active />;
    if (status === 'authenticated' && destination) return <Redirect to={destination} />;
    return (
        <AuthProvider authority={config.OIDCUrl} redirect_uri={`${window.location.origin}/ticket/landing`} client_id={config.clientIdRegister}>
            <RegistrationInner />
        </AuthProvider>
    );
};

Registration.propTypes = {
    config: PropTypes.shape({
        OIDCUrl: PropTypes.string,
        clientIdRegister: PropTypes.string,
        clientId: PropTypes.string
    }).isRequired
};
